import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Stack,
  Checkbox,
  FormLabel,
  CloseButton,
  VStack
} from '@chakra-ui/react'
import { useForm, FormProvider } from 'react-hook-form'
import FormInput from 'components/Chakra/FormInput'
import { IProcess, IProcessWithRelations } from 'interfaces/process'
import FormDropdownInput from 'components/FormInputs/DropdownInput'
import { getDocumentTypes } from 'API/documentType'
import { IDocumentType } from 'interfaces/documentType'
import { IFilter, IOption, IOptionWithIcon } from 'types/common'
import { useStores } from 'hooks/useStores'
import { CloseIcon } from '@chakra-ui/icons'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import { duplicateProcess } from 'API/process'
import routes from 'routes'
import { toast } from 'react-toastify'
import { AuthRoleIdEnum } from 'constants/user'

interface ICopyProcessModalProps {
  isOpen: boolean
  onClose: () => void
  processId: number
}

export interface IProcessDuplicate {
  documentTypeId: number
  copyCollection?: boolean
  copySharing?: boolean
  copyTags?: boolean
  copyNote?: boolean
  name: string
  collectionIds?: number[]
  groupIds?: number[]
}

interface IProcessDuplicateForm extends IProcessDuplicate {
  documentType: IOptionWithIcon<number>
  collections: IOption<number>[]
  groups: IOption<number>[]
}

const CopyProcessModal = ({ isOpen, onClose, processId }: ICopyProcessModalProps) => {
  const { authStore, processStore, collectionStore, userStore } = useStores()
  const { selectedProcess, process } = processStore
  const sourceProcess = selectedProcess?.id ? selectedProcess : process
  const name = sourceProcess?.name ?? 'Copy of Processes'
  const documentType = sourceProcess?.documentType ?? ''
  const isBasicUser = authStore?.userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
  const methods = useForm<IProcessDuplicateForm>({
    defaultValues: {
      name: `Copy of ${name}`,
      documentType: {
        label: documentType?.name ?? '',
        value: documentType?.id ?? 0
      },
      collections: [],
      groups: [],
      copyCollection: true,
      copySharing: false,
      copyTags: false,
      copyNote: false
    }
  })
  const [documentTypeOptions, setDocumentTypeOptions] = useState<IOptionWithIcon<number>[]>([])
  const [collectionOptions, setCollectionOptions] = useState<IOption<number>[]>([])
  const [groupOptions, setGroupOptions] = useState<IOption<number>[]>([])
  const navigate = useNavigate()

  const { handleSubmit, control } = methods
  async function fetchDocumentTypeOptions(): Promise<void> {
    const filter: IFilter<IDocumentType> = {
      where: { organizationId: authStore?.userDetail?.organizationId },
      include: ['iconBuilder'],
      order: ['name ASC']
    }
    const documentTypes = await getDocumentTypes(filter)
    const documentTypeOptions = documentTypes.map(documentType => ({
      label: documentType.name ?? '',
      value: Number(documentType.id),
      icon: documentType?.iconBuilder
    }))
    setDocumentTypeOptions(documentTypeOptions)
  }

  async function fetchCollectionAndGroupOptions(): Promise<void> {
    if (!authStore?.userDetail) {
      console.log('No user detail available')
      return
    }

    // For basic users, check if user group memberships are loaded
    if (isBasicUser) {
      const currentUserGroupMembers = userStore.currentUserGroupMembers || []
      console.log('User group memberships loaded:', currentUserGroupMembers.length, currentUserGroupMembers)

      if (currentUserGroupMembers.length === 0) {
        console.warn('No group memberships found for basic user. Collections and groups will be empty.')
        console.log('Will retry fetching group memberships in 1 second...')
        // Retry after a short delay in case group memberships are still loading
        setTimeout(() => {
          const retryGroupMembers = userStore.currentUserGroupMembers || []
          if (retryGroupMembers.length > 0) {
            console.log('Group memberships now available after retry:', retryGroupMembers.length)
            fetchCollectionAndGroupOptions()
          } else {
            console.log('Still no group memberships after retry. User might not belong to any groups.')
          }
        }, 1000)
        return
      }
    }

    try {
      console.log('Fetching collections and groups for user:', authStore.userDetail.id, 'isBasicUser:', isBasicUser)
      const [collections, groups] = await Promise.all([
        collectionStore.fetchCollectionsWithEditorPermissions(authStore.userDetail),
        collectionStore.fetchGroupsWithEditorPermissions(authStore.userDetail)
      ])
      setCollectionOptions(collections)
      setGroupOptions(groups)
    } catch (error) {
      console.error('Error fetching collection and group options:', error)
    }
  }

  useEffect(() => {
    fetchDocumentTypeOptions()
    if (isOpen) {
      fetchCollectionAndGroupOptions()
    }
  }, [authStore?.userDetail, isBasicUser, isOpen, userStore.currentUserGroupMembers])

  useEffect(() => {
    return () => {
      methods.reset()
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      if (selectedProcess?.id || process?.id) {
        methods.reset({
          name: `Copy of ${name}`,
          documentType: {
            label: sourceProcess?.documentType?.name,
            value: sourceProcess?.documentType?.id
          },
          collections: [],
          groups: [],
          copyCollection: true,
          copySharing: false,
          copyTags: false,
          copyNote: false
        })
      }
    }
  }, [isOpen, selectedProcess, process])

  async function onSubmit(data: IProcessDuplicateForm) {
    try {
      // Only include collectionIds and groupIds for basic users
      const collectionIds = isBasicUser ? data.collections?.map(collection => collection.value) || [] : []
      const groupIds = isBasicUser ? data.groups?.map(group => group.value) || [] : []

      const newProcess: IProcess = await duplicateProcess(processId, {
        documentTypeId: data?.documentType?.value,
        copyCollection: data?.copyCollection,
        copySharing: data?.copySharing,
        copyTags: data?.copyTags,
        copyNote: data?.copyNote,
        name: data?.name,
        collectionIds: isBasicUser && collectionIds.length > 0 ? collectionIds : undefined,
        groupIds: isBasicUser && groupIds.length > 0 ? groupIds : undefined
      })
      if (newProcess?.id && processId !== newProcess?.id) {
        toast.success('Process duplicated successfully')
        navigate(routes.processes.processId.value(String(newProcess?.id)))
      } else {
        throw new Error('Error duplicating process')
      }
    } catch (error) {
      toast.error('Error duplicating process')
    }
    onClose() // Close the modal after form submission
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" autoFocus>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton border="none" backgroundColor="white" />
        <ModalHeader
          fontWeight="500"
          fontSize="18px"
          lineHeight="28px"
          color="gray.800"
          borderBottom="1px solid #E2E8F0"
          fontFamily="Inter"
        >
          Copy process
        </ModalHeader>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody paddingY={6}>
              <Stack spacing={6}>
                <FormInput
                  name="name"
                  label="Process name"
                  placeholder="Enter process name"
                  isRequired={true}
                  rules={{ required: 'Process name is required' }}
                />
                <Stack spacing={0}>
                  <FormLabel fontWeight={500} fontSize="16px" lineHeight="24px" color="gray.700">
                    Document Type
                  </FormLabel>
                  <FormDropdownInput
                    controllerProps={{ name: 'documentType', control }}
                    inputProps={{
                      name: 'documentType',
                      options: documentTypeOptions,
                      hasIcon: true,
                      hasNoSeparator: true,
                      placeholder: 'Choose Document type'
                    }}
                  />
                </Stack>

                {isBasicUser && (
                  <>
                    <Stack spacing={0}>
                      <FormLabel fontWeight={500} fontSize="16px" lineHeight="24px" color="gray.700">
                        Share with Collections
                      </FormLabel>
                      <FormDropdownInput
                        controllerProps={{ name: 'collections', control }}
                        inputProps={{
                          options: collectionOptions,
                          isMulti: true,
                          hasNoSeparator: true,
                          placeholder: 'Select collections to share with'
                        }}
                      />
                    </Stack>

                    <Stack spacing={0}>
                      <FormLabel fontWeight={500} fontSize="16px" lineHeight="24px" color="gray.700">
                        Share with Groups
                      </FormLabel>
                      <FormDropdownInput
                        controllerProps={{ name: 'groups', control }}
                        inputProps={{
                          options: groupOptions,
                          isMulti: true,
                          hasNoSeparator: true,
                          placeholder: 'Select groups to share with'
                        }}
                      />
                    </Stack>
                  </>
                )}

                <Stack spacing={1}>
                  {!isBasicUser && (
                    <FormInput name="copyCollection">
                      <Checkbox color="gray.700" {...methods.register('copyCollection')}>
                        Collection Inclusion
                      </Checkbox>
                    </FormInput>
                  )}
                  {!isBasicUser && (
                    <FormInput name="copySharing">
                      <Checkbox color="gray.700" {...methods.register('copySharing')}>
                        Copy all share settings
                      </Checkbox>
                    </FormInput>
                  )}
                  <FormInput name="copyTags">
                    <Checkbox color="gray.700" {...methods.register('copyTags')}>
                      Copy all existing tags
                    </Checkbox>
                  </FormInput>
                  <FormInput name="copyNote">
                    <Checkbox color="gray.700" {...methods.register('copyNote')}>
                      Copy all notes
                    </Checkbox>
                  </FormInput>
                </Stack>
              </Stack>
            </ModalBody>

            <ModalFooter borderTop="1px solid #E2E8F0" gap={4}>
              <Button variant="white" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default observer(CopyProcessModal)
